import React, { useState } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate, Navigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import BrandMark from './BrandMark';
import CreditUsageSection from './CreditUsageSection';
import { isAuthenticated } from '../services/apiClient';
import { useCurrentUser, useLogout } from '../hooks/useAuth';
import { sidebarTokens, BRAND } from '../theme';
import { displayName, initialsOf } from '../utils/user';

const NAV_ITEMS = [
  { label: 'Tasks', path: '/tasks', icon: <ChecklistRoundedIcon /> },
  { label: 'Time Logs', path: '/time-logs', icon: <ScheduleRoundedIcon /> },
  { label: 'Categories', path: '/categories', icon: <FolderRoundedIcon /> },
  { label: 'Analytics', path: '/analytics', icon: <BarChartRoundedIcon /> },
  { label: 'AI Assistant', path: '/assistant', icon: <AutoAwesomeRoundedIcon /> },
];

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ gap: 1.25, px: 2.5 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'primary.main',
            color: '#fff',
          }}
        >
          <BrandMark fontSize="small" />
        </Box>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, letterSpacing: '-0.01em' }}>
          {BRAND.name}
        </Typography>
      </Toolbar>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
      <List sx={{ px: 1.5, py: 2, flexGrow: 1 }}>
        {NAV_ITEMS.map((item) => {
          const selected = location.pathname.startsWith(item.path);
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={selected}
                onClick={() => {
                  navigate(item.path);
                  onNavigate?.();
                }}
                sx={{
                  borderRadius: 2,
                  color: sidebarTokens.itemColor,
                  '&:hover': { bgcolor: sidebarTokens.itemHoverBg },
                  '&.Mui-selected': {
                    bgcolor: sidebarTokens.itemActiveBg,
                    color: sidebarTokens.itemActiveColor,
                    '&:hover': { bgcolor: sidebarTokens.itemActiveBg },
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontWeight: selected ? 600 : 500, fontSize: 14.5 }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Box sx={{ px: 2.5, pb: 2 }}>
        <Typography variant="caption" component="p" sx={{ color: 'rgba(255,255,255,0.35)', mb: 0.75 }}>
          {BRAND.tagline}
        </Typography>
        <Typography variant="caption" sx={{ '& a': { color: 'rgba(255,255,255,0.5)' } }}>
          <Link component={RouterLink} to="/privacy" underline="hover">
            Privacy
          </Link>
          {' · '}
          <Link component={RouterLink} to="/terms" underline="hover">
            Terms
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}

const AppLayout: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useLogout();
  const { data: user } = useCurrentUser();

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  const currentNav = NAV_ITEMS.find((i) => location.pathname.startsWith(i.path));
  const pageTitle =
    currentNav?.label ?? (location.pathname.startsWith('/profile') ? 'Profile' : BRAND.name);

  const drawerSx = {
    width: sidebarTokens.width,
    '& .MuiDrawer-paper': {
      width: sidebarTokens.width,
      bgcolor: sidebarTokens.background,
      borderRight: 'none',
    },
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {isDesktop ? (
        <Drawer variant="permanent" sx={drawerSx}>
          <SidebarContent />
        </Drawer>
      ) : (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={drawerSx}
        >
          <SidebarContent onNavigate={() => setMobileOpen(false)} />
        </Drawer>
      )}

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: 'rgba(244,245,250,0.85)',
            backdropFilter: 'blur(8px)',
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Toolbar sx={{ gap: 1 }}>
            {!isDesktop && (
              <IconButton edge="start" onClick={() => setMobileOpen(true)} aria-label="Open navigation">
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {pageTitle}
            </Typography>
            <Tooltip title="Account">
              <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} size="small" aria-label="Account menu">
                <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: 15 }}>
                  {user ? initialsOf(user) : 'U'}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={menuAnchor}
              open={!!menuAnchor}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {user && (
                <Box sx={{ px: 2, pt: 1, pb: 1.5, maxWidth: 260 }}>
                  <Typography variant="subtitle2" noWrap>
                    {displayName(user)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap component="div">
                    {user.email}
                  </Typography>
                </Box>
              )}
              {user && <Divider />}
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  navigate('/profile');
                }}
              >
                <ListItemIcon>
                  <PersonRoundedIcon fontSize="small" />
                </ListItemIcon>
                Profile
              </MenuItem>
              <Divider />
              <CreditUsageSection />
              <Divider />
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  logout.mutate();
                }}
              >
                <ListItemIcon>
                  <LogoutRoundedIcon fontSize="small" />
                </ListItemIcon>
                Sign out
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AppLayout;
